import React from 'react';
import { motion } from 'framer-motion';
import {
  Image,
  Camera,
  Radio,
  CalendarClock,
  BookOpen,
  Sparkles,
  ArrowUpRight,
} from 'lucide-react';
import { EventCountdown } from '../EventCountdown.jsx';

const TILES = {
  timeline: {
    route: 'timeline',
    icon: CalendarClock,
    eyebrow: "What's on",
    label: 'Timeline',
    blurb: "What's happening, and when.",
  },
  livestream: {
    route: 'livestream',
    icon: Radio,
    eyebrow: 'Watching from afar',
    label: 'Livestream',
    blurb: "Can't be here? Watch it live.",
  },
  photobooth: {
    route: 'photobooth',
    icon: Camera,
    eyebrow: 'Join in',
    label: 'Photo Booth',
    blurb: "Snap a pic — it'll show up on the wall.",
  },
  gallery: {
    route: 'gallery',
    icon: Image,
    eyebrow: 'Look back',
    label: 'Gallery',
    blurb: 'Every photo from the celebration.',
  },
  guestbook: {
    route: 'guestbook',
    icon: BookOpen,
    eyebrow: 'Leave a note',
    label: 'Guestbook',
    blurb: 'Write something for us to keep.',
  },
  game: {
    route: 'game',
    icon: Sparkles,
    eyebrow: 'Our story',
    label: 'The Story Game',
    blurb: 'Play through our favorite memories.',
  },
};

// What matters changes through the celebration, so the tile set and its order
// change with it — the first entry leads as the hero.
//
// Before the day the photo booth and the livestream have nothing in them, so
// they only join once there are events. Afterwards everything stays: the
// livestream holds the recordings and the booth still takes photos from the
// day, so nothing the site offered on the day goes missing.
const BY_PHASE = {
  approaching: ['timeline', 'gallery', 'guestbook', 'game'],
  eventday: ['timeline', 'livestream', 'photobooth', 'gallery', 'guestbook', 'game'],
  live: ['livestream', 'timeline', 'photobooth', 'gallery', 'guestbook', 'game'],
  after: ['gallery', 'livestream', 'guestbook', 'photobooth', 'timeline', 'game'],
};

// Tile copy that reads wrong once it's all over — "watch it live" about a
// finished broadcast. Only the differing fields are listed.
const TILE_OVERRIDES = {
  after: {
    livestream: { eyebrow: 'Watch it again', blurb: 'Every stream from the celebration.' },
    photobooth: { eyebrow: 'Share yours', blurb: 'Add your photos from the day to the wall.' },
    timeline: { eyebrow: 'How it went', blurb: 'The schedule and the venues.' },
  },
};

const tileFor = (phase, key) => ({ ...TILES[key], ...TILE_OVERRIDES[phase]?.[key] });

const HEADINGS = {
  approaching: {
    eyebrow: 'Almost time',
    blurb: 'The celebration is nearly here — this is how it all unfolds.',
  },
  eventday: { eyebrow: "Today's the day", blurb: 'Everything you need for today, in one place.' },
  live: { eyebrow: 'Happening now', blurb: 'Everything you need for today, in one place.' },
  after: {
    eyebrow: 'Thank you for celebrating with us',
    blurb: 'The photos, the streams and the guestbook are all still here.',
  },
};

// A bento grid rather than an even one, so the page has a single clear thing
// to look at instead of several equal claims on attention. The logo, name and
// countdown carry over from the invitation state above it, so this reads as
// the same page having evolved rather than a different site.
export function HomeDayOf({ isDarkMode, phase, currentEvent, todayTitle, onNavigate, onStartGame }) {
  const muted = isDarkMode ? 'text-slate-300' : 'text-slate-600';
  const eyebrowClass = 'font-accent text-[11px] tracking-[0.4em] uppercase text-secondary mb-2.5';

  const keys = BY_PHASE[phase] || BY_PHASE.eventday;
  const [heroKey, ...restKeys] = keys;
  const hero = tileFor(phase, heroKey);
  const rest = restKeys.map((k) => tileFor(phase, k));
  const headings = HEADINGS[phase] || HEADINGS.eventday;

  // "Today is Melse" beats "Today's the day" — the all-day entry on the
  // calendar is the couple's own name for what today is, so it says what's
  // going on before a single tile is read. Falls back to the static wording
  // when a day carries no all-day entry.
  const isEventDay = phase === 'eventday' || phase === 'live';
  const eyebrowText =
    isEventDay && todayTitle ? `Today is ${todayTitle}` : headings.eyebrow;

  // The game keeps its opening swell wherever it's launched from.
  const go = (route) => (route === 'game' && onStartGame ? onStartGame() : onNavigate(route));

  const surface = isDarkMode
    ? 'bg-slate-900/70 border-white/10 hover:border-purple-400/40'
    : 'bg-white/85 border-slate-200 hover:border-purple-300 shadow-sm';

  return (
    <div className="relative z-10 w-full max-w-md mx-auto text-center flex flex-col items-center">
      {/* Still counting down through the final fortnight; past that it has
          nothing left to say and the schedule takes over. */}
      {phase === 'approaching' && (
        <div className="mb-6 w-full">
          <EventCountdown />
        </div>
      )}

      <div className="w-24 h-24 sm:w-28 sm:h-28 mb-3 flex items-center justify-center">
        <img
          src="/wedding_logo.png"
          className="max-w-full max-h-full object-contain"
          alt="Wedding Logo"
          width="600"
          height="572"
        />
      </div>

      <p className={eyebrowClass}>{eyebrowText}</p>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight drop-shadow-sm">
        Yisehak &amp; Lydia's Wedding
      </h1>

      {/* While something is actually running, say what it is — the single most
          useful sentence on the page at that moment. */}
      {phase === 'live' && currentEvent ? (
        <p className={`mt-2.5 mb-8 text-sm flex items-center justify-center gap-2 ${muted}`}>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary" />
          </span>
          <span className="font-bold text-secondary">{currentEvent.title}</span> is underway
        </p>
      ) : (
        <p className={`mt-2 mb-8 text-sm ${muted}`}>{headings.blurb}</p>
      )}

      <div className="grid grid-cols-2 gap-3 w-full">
        <motion.button
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
          onClick={() => go(hero.route)}
          className="col-span-2 group relative overflow-hidden rounded-3xl p-5 text-left bg-gradient-to-br from-primary to-secondary text-white shadow-[0_18px_45px_-20px_rgba(194,50,106,0.8)] transition-transform hover:scale-[1.01] active:scale-[0.99]"
        >
          <span className="absolute -right-6 -bottom-8 opacity-15" aria-hidden="true">
            <hero.icon size={132} strokeWidth={1.25} />
          </span>
          <span className="relative flex flex-col gap-1">
            <span className="font-accent text-[10px] tracking-[0.35em] uppercase opacity-80">
              {hero.eyebrow}
            </span>
            <span className="font-display font-bold text-2xl">{hero.label}</span>
            <span className="text-xs opacity-90 max-w-[80%]">{hero.blurb}</span>
          </span>
          <ArrowUpRight
            size={20}
            className="absolute top-5 right-5 opacity-70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </motion.button>

        {rest.map(({ route, icon: Icon, label, blurb }, i) => (
          <motion.button
            key={route}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.06 * (i + 1), ease: [0.25, 1, 0.5, 1] }}
            onClick={() => go(route)}
            // The last tile fills the row when the remainder is odd, so the
            // grid never ends on a lonely half-width card.
            className={`group flex flex-col gap-2 rounded-2xl border p-4 text-left transition-all hover:-translate-y-0.5 ${surface} ${
              rest.length % 2 === 1 && i === rest.length - 1 ? 'col-span-2' : ''
            }`}
          >
            <span className="flex items-center justify-between">
              <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-tr from-primary to-secondary text-white shadow-sm transition-transform group-hover:scale-105">
                <Icon size={17} />
              </span>
              <ArrowUpRight
                size={15}
                className={`${muted} opacity-0 group-hover:opacity-100 transition-opacity`}
              />
            </span>
            <span className="font-display font-bold text-[15px]">{label}</span>
            <span className={`text-[11px] leading-snug ${muted}`}>{blurb}</span>
          </motion.button>
        ))}
      </div>

      {/* No RSVP link here, deliberately: by the time the hub is up replies are
          in, and pointing a guest at a form they've already filled in — or
          that's closed to them — is worse than not offering it. Everything the
          hub offers is a tile. */}
    </div>
  );
}
